import React, { useRef, useState, useImperativeHandle, useEffect } from "react";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { Button, IconButton } from '@mui/material';
import RecordRTC from "recordrtc";
import { storage } from "firebaseConfig";
import {
  Delete,
  
  PauseCircleFilledOutlined,
  PlayArrowOutlined,
  RecordVoiceOverOutlined,
} from '@mui/icons-material';
import { ref as fireRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";
import moment from "moment";
import Loading from "components/Loading";

import {Mic} from '@mui/icons-material';
import * as Sentry from '@sentry/react';

// Styled Components
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 1px dashed #ccc;
`;

const Text = styled.span`
  color: ${COLOR.black300};
  display: block;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 24px;
  text-align: center;
`;

const Counter = styled.span`
  color: ${COLOR.green100};
  display: block;
  font-size: 32px;
  font-weight: 600;
  line-height: 80px;
  margin-bottom: 16px;
  text-align: center;
`;

const Disclosure = styled.span`
  color: ${COLOR.yellow};
  display: block;
  font-size: 12px;
  line-height: 12px;
  margin-bottom: 10px;
  text-align: center;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0px;
  }
`;

const LivePreview = styled.video`
  width: 100%;
  max-width: 720px;
  margin-bottom: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #000;
`;

const VideoPreview = styled.video`
  width: 100%;
  max-width: 720px;
  margin-bottom: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const RecordingIndicator = styled.div`
  width: 20px;
  height: 20px;
  background-color: red;
  border-radius: 50%;
  margin-bottom: 16px;
`;
const RecordButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(45deg, #9b4dca, #00c6ff); /* Updated gradient with purple to blue */
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;
  border-radius:4px !important;
  min-height:40px;
  &:hover {
    background: linear-gradient(45deg, #00c6ff, #9b4dca); /* Reverse gradient on hover */
    transform: scale(1.05); /* Slightly enlarge the button on hover */
  }

  &:focus {
    outline: none;
  }

  svg {
    margin-right: 8px; /* Space between the icon and the text */
  }
`;

const VideoRecorder = React.forwardRef(
  (
    {
      handleVideo,
      message = "Record a video",
      hasSaveButton,
      videoSource,
      isPreview = false,
      maxTime,
      type = "",
      student = false,
      isMobileDevice = false,
      setButtonDisabled = (value) => null,
      isRecordingStatus = (status) => {},
      handleDeleteAudioVideo=() =>{},
      from="",
      showRemoveVideoButton = false,
      onRegisterMediaElement,
      onMediaPlay
    },
    ref
  ) => {
    const [inUrlProcess, setInUrlProcess] = useState(false);
    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);
    const [reRecording, setReRecording] = useState(false);
    const [video, setVideo] = useState(videoSource || null);
    const [showLivePreview, setShowLivePreview] = useState(false);
    const [stream, setStream] = useState(null);
    const [blob, setBlob] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [counter, setCounter] = useState(maxTime);
    const studentReviewSubmission =
      type === "student-submission-review" && isMobileDevice;
    const recorderRef = useRef(null);
    const liveVideoRef = useRef(null);
    const recordedVideoElementRef = useRef(null);
    const timerRef = useRef(null);
    const saveButtonRef = useRef(null);
    const playRetryTimeoutRef = useRef(null);

    useImperativeHandle(ref, () => ({
      start: startRecording,
      stop: stopRecording,
      pause: pauseRecording,
      resume: resumeRecording,
      reset: resetRecording,
    }));

    useEffect(() => {
      if (recording && counter > 0) {
        timerRef.current = setInterval(() => {
          setCounter((prev) => prev - 1);
        }, 1000);
      } else if (counter === 0) {
        stopRecording();
      }
      return () => clearInterval(timerRef.current);
    }, [recording, counter]);

    useEffect(() => {
      if (recording) {
        isRecordingStatus(true);
      } else if (!recording && video) {
        isRecordingStatus(false);
      }
    }, [recording]);

    useEffect(() => {
      if (videoSource && videoSource.length > 0) {
        setVideo(videoSource);
        setRecording(false);
      }
    }, [videoSource]);

    useEffect(() => {
      if (!video) return;
      if (!recordedVideoElementRef.current) return;
      if (typeof onRegisterMediaElement !== "function") return;
      onRegisterMediaElement(recordedVideoElementRef.current);
    }, [video, onRegisterMediaElement]);

    useEffect(() => {
      setCounter(maxTime);
    }, [maxTime]);

    useEffect(() => {
      return () => {
        if (playRetryTimeoutRef.current) {
          clearTimeout(playRetryTimeoutRef.current);
          playRetryTimeoutRef.current = null;
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      };
    }, [stream]);

    const convertToMP4 = async (webmBlob) => {
      return new Promise((resolve, reject) => {
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'video/mp4',
          videoBitsPerSecond: 2500000,
          audioBitsPerSecond: 128000
        });
        
        let chunks = [];
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          const mp4Blob = new Blob(chunks, { type: 'video/mp4' });
          resolve(mp4Blob);
        };
        mediaRecorder.onerror = (error) => reject(error);
        
        // Convert the WebM blob to arrayBuffer and then to MP4
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result;
          mediaRecorder.start();
          mediaRecorder.stop();
        };
        reader.readAsArrayBuffer(webmBlob);
      });
    };

    // Video recording with enhanced constraints
    const startRecording = async () => {
      if (inUrlProcess) return;
      let activeStream = null;
      
      try {
        const constraints = {
          video: true,
          audio: true
        };

        const userStream = await navigator.mediaDevices.getUserMedia(constraints);
        activeStream = userStream;
        setStream(userStream);
        setShowLivePreview(true);

        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = userStream;
          liveVideoRef.current.load();
          
          await new Promise((resolve) => {
            const playVideo = async () => {
              try {
               if (!liveVideoRef.current) {
                resolve();
                return;
               }
              
               if (userStream && userStream.getTracks().some(track => track.readyState === 'live')) {
                await liveVideoRef.current.play();
                resolve();
               } else {
                resolve();
               }
              } catch (error) {
                console.error('Error playing video:', error);
                if (playRetryTimeoutRef.current) {
                  clearTimeout(playRetryTimeoutRef.current);
                }
                
                if (userStream && userStream.getTracks().some(track => track.readyState === 'live')) {
                  playRetryTimeoutRef.current = setTimeout(playVideo, 100);
                } else {
                  resolve();
                }
              }
            };
            liveVideoRef.current.onloadedmetadata = playVideo;
          });
        }

        try {
          const hdConstraints = {
            video: {
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30, max: 60 },
              aspectRatio: 16 / 9,
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 48000,
            },
          };

          const hdStream = await navigator.mediaDevices.getUserMedia(hdConstraints);
          
          if (userStream && userStream !== hdStream) {
            userStream.getTracks().forEach(track => track.stop());
          }
          
          activeStream = hdStream;
          
          if (liveVideoRef.current) {
            liveVideoRef.current.srcObject = hdStream;
            setStream(hdStream);
          }
        } catch (hdError) {
          console.log('HD stream not available, using standard quality:', hdError);
        }

        const mimeType = MediaRecorder.isTypeSupported('video/mp4') 
          ? 'video/mp4' 
          : 'video/webm';

        if (!activeStream || !activeStream.active) {
          throw new Error('MediaStream is not active');
        }

        recorderRef.current = new RecordRTC(activeStream, {
          type: 'video',
          mimeType: mimeType,
          videoBitsPerSecond: 2500000,
          audioBitsPerSecond: 128000,
          frameInterval: 30,
          videoCodec: 'H264',
          recorderType: RecordRTC.MediaStreamRecorder,
          video: {
            width: 1920,
            height: 1080
          }
        });

        recorderRef.current.startRecording();
        setCounter(maxTime);
        setRecording(true);
        setPaused(false);
      } catch (error) {
        Sentry.captureException(`556 Error starting recording: ${error}`);
        console.error("Error starting recording:", error);
        alert("Error accessing camera or microphone. Please check permissions.");
        if(setButtonDisabled) {
          setButtonDisabled(false);
        }
      }
    };

    const secondsToTime = (secs, description = false) => {
      let hours = Math.floor(secs / (60 * 60));
      let divisor_for_minutes = secs % (60 * 60);
      let minutes = Math.floor(divisor_for_minutes / 60);
      let divisor_for_seconds = divisor_for_minutes % 60;
      let seconds = Math.ceil(divisor_for_seconds);
      let string = "";
      if (description) {
        if (hours > 0) string += hours + " hours, ";
        if (minutes > 0) string += minutes + " minutes";
        if (seconds > 0)
          string += ", " + (seconds < 10 ? "0" : "") + seconds + " seconds";
      } else {
        if (hours > 0) string += hours + ":";
        if (minutes > 0) string += (minutes < 10 ? "0" : "") + minutes;
        else string += "00";
        if (seconds > 0) string += ":" + (seconds < 10 ? "0" : "") + seconds;
        else string += ":00";
      }
      return string;
    };

    const pauseRecording = () => {
      if (inUrlProcess || !recording || paused || !recorderRef.current) return;
      recorderRef.current.pauseRecording();
      setPaused(true);
      clearInterval(timerRef.current);
    };

    const resumeRecording = () => {
      if (inUrlProcess || !recording || !paused || !recorderRef.current) return;
      recorderRef.current.resumeRecording();
      setPaused(false);
      timerRef.current = setInterval(() => {
        setCounter((prev) => prev - 1);
      }, 1000);
    };

    const stopRecording = async () => {
      if (!recorderRef.current || inUrlProcess) return;
      setInUrlProcess(true);

      if (playRetryTimeoutRef.current) {
        clearTimeout(playRetryTimeoutRef.current);
        playRetryTimeoutRef.current = null;
      }

      recorderRef.current.stopRecording(async () => {
        try {
          let recordedBlob = recorderRef.current.getBlob();
          
          // If the recorded blob is WebM, convert to MP4
          if (recordedBlob.type.includes('webm')) {
            try {
              recordedBlob = await convertToMP4(recordedBlob);
            } catch (conversionError) {
              Sentry.captureException(`557 Error converting to MP4: ${conversionError}`);
              console.error("Error converting to MP4:", conversionError);
              // Continue with WebM if conversion fails
            }
          }

          const videoUrl = URL.createObjectURL(recordedBlob);
          
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }

          await uploadVideo(recordedBlob);
          setBlob(recordedBlob);
          setVideo(videoUrl);
          setRecording(false);
         if (timerRef.current) {
          clearInterval(timerRef.current);
           timerRef.current = null;
         }
          setCounter(maxTime);
          setShowLivePreview(false);
        } catch (error) {
          Sentry.captureException(`558 Error processing recording: ${error}`);
          console.error("Error processing recording:", error);
          alert("Error processing the recording. Please try again.");
        } finally {
          setInUrlProcess(false);
          if(setButtonDisabled) {
            setButtonDisabled(false);
          }
        }
      });
    };

    const resetRecording = () => {
      if (playRetryTimeoutRef.current) {
        clearTimeout(playRetryTimeoutRef.current);
        playRetryTimeoutRef.current = null;
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      if (recorderRef.current) {
        try {
          recorderRef.current.stopRecording();
        } catch (error) {
          console.error('Error stopping recorder during reset:', error);
        }
        recorderRef.current = null;
      }
      
      setVideo(null);
      setReRecording(true);
      setBlob(null);
      setRecording(false);
      setPaused(false);
      setShowLivePreview(false);
      setCounter(maxTime);
    };

    const uploadVideo = async (blob1) => {
      if(setButtonDisabled) {
        setButtonDisabled(true);
      }
      
      let finalblob = blob1 ? blob1 : blob;
      if (!finalblob) return;
      
      setReRecording(true);
      setUploading(true);
      setInUrlProcess(true);

      // Change the extension to .mp4
      const location = `/videos/${moment().format("YYYY-MM-DD")}-${v4()}.mp4`;
      const videoRef = fireRef(storage, location);

      try {
        await uploadBytes(videoRef, finalblob);
        const downloadUrl = await getDownloadURL(videoRef);
        handleVideo(downloadUrl);
        setInUrlProcess(false);
      } catch (error) {
        Sentry.captureException(`559 Error uploading video: ${error}`);
        console.error("Error uploading video:", error);
        alert("Upload failed. Please try again.");
      } finally {
        setUploading(false);
        if(setButtonDisabled) {
          setButtonDisabled(false);
        }
      }
    };

    const handleDeleteVideo = () => {
      resetRecording();
      handleDeleteAudioVideo("video");
    };

    const clearUploadedVideoForBuilder = () => {
      resetRecording();
      handleVideo("");
    };

    return (
      <Wrapper>
        {/* Recording Indicator */}
        {recording && !paused && <RecordingIndicator />}

        {/* Live Video Stream */}
        {showLivePreview && (
          <LivePreview
            ref={liveVideoRef}
            autoPlay
            playsInline
            muted
          />
        )}

        {!recording && video ? (
          <>
            <VideoPreview
              ref={recordedVideoElementRef}
              src={video}
              controls
              onPlay={() => {
                if (typeof onMediaPlay === "function" && recordedVideoElementRef.current) {
                  onMediaPlay(recordedVideoElementRef.current);
                }
              }}
            />
            <div className="flex items-center" style={{ gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              <RecordButton
              className='reRecordBtn'
              variant="contained"
              color="primary"
              onClick={resetRecording}
            >
              <Mic />Re-record</RecordButton>
            {showRemoveVideoButton ? (
              <Button
                variant="outlined"
                color="error"
                onClick={clearUploadedVideoForBuilder}
                sx={{ minHeight: 40, borderRadius: "8px" }}
              >
                Remove
              </Button>
            ) : null}

            {from === "instruction" && (
              <IconButton onClick={() => {
                if(handleDeleteVideo){
                handleDeleteVideo()
              }
              }}>
                <Delete color='error' />
                </IconButton>
            )}
          
            </div>
           
          </>
        ) : (
          <>
            {counter === null && (
              <>
                <Text style={{ marginBottom: "5px" }}>
                  {message ??
                    `Click "Record" below. You will have ${secondsToTime(
                      maxTime,
                      true
                    )} to record your answer.`}
                </Text>
                {!studentReviewSubmission && (
                  <Disclosure>
                    Place your microphone close for better audio quality.
                  </Disclosure>
                )}
              </>
            )}
            {maxTime && counter > 0 && (
              <>
                {!studentReviewSubmission && (
                  <Disclosure>
                    Place your microphone close for better audio quality.
                  </Disclosure>
                )}
                <Counter
                  className={`${
                    studentReviewSubmission ? "m-none ft-17px" : ""
                  }`}
                >
                  {secondsToTime(counter)}
                </Counter>
              </>
            )}
            <Row
              className={`${
                studentReviewSubmission ? "m-none mb-16px items-center" : ""
              }`}
            >
              {studentReviewSubmission ? (
                <button
                  disabled={isPreview}
                  color="primary"
                  variant="contained"
                  className="record-button-student-review items-center flex"
                  onClick={() => {
                    if (!isPreview) {
                      if (recording && paused) {
                        resumeRecording();
                      } else if (recording && !paused) {
                        pauseRecording();
                      } else {
                        startRecording();
                      }
                    }
                  }}
                  style={{ marginRight: "8px" }}
                >
                  {recording && paused ? (
                    <PlayArrowOutlined style={{ color: "#03989e" }} />
                  ) : recording && !paused ? (
                    <PauseCircleFilledOutlined style={{ color: "#03989e" }} />
                  ) : (
                    <RecordVoiceOverOutlined style={{ color: "#03989e" }} />
                  )}
                  {/* {recording && paused ? "Resume" : recording && !paused ? "Pause" : "Record"} */}
                </button>
              ) : (
                <Button
                  disabled={isPreview}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    if (!isPreview) {
                      if (recording && paused) {
                        resumeRecording();
                      } else if (recording && !paused) {
                        pauseRecording();
                      } else {
                        startRecording();
                      }
                    }
                  }}
                  style={{ marginRight: "8px" }}
                >
                  {recording && paused
                    ? "Resume"
                    : recording && !paused
                    ? "Pause"
                    : "Record"}
                </Button>
              )}

              <Button
                style={{ height: studentReviewSubmission ? "31px" : "" }}
                variant="outlined"
                disabled={!recording}
                ref={saveButtonRef}
                onClick={() => stopRecording()}
              >
                {inUrlProcess && <Loading size={14} />}
                {hasSaveButton ? "Save" : "Stop"}
              </Button>
            </Row>
          </>
        )}
        {/* Recorded Video Preview */}
        {/* {video ? (
          <>
            <VideoPreview src={video} controls />
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={resetRecording}
              >
                Re-record
              </Button>
              {(hasSaveButton || !student) && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={uploadVideo}
                  style={{ marginLeft: "8px" }}
                  ref={saveButtonRef}
                  disabled={!video || uploading}
                >
                  {uploading ? <Loading size={16} /> : "Save"}
                </Button>
              )}
            </div>
          </>
        ) : (
          <div>
            <Text style={{ marginBottom: "5px" }}>{message}</Text>
            {maxTime && counter > 0 && (
              <Counter>{secondsToTime(counter)}</Counter>
            )}

            {recording && !paused && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={pauseRecording}
                style={{ marginRight: "8px" }}
              >
                Pause
              </Button>
            )}

            {recording && paused && (
              <Button
                variant="outlined"
                color="primary"
                onClick={resumeRecording}
                style={{ marginRight: "8px" }}
              >
                Resume
              </Button>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={recording ? stopRecording : startRecording}
              // disabled={paused}
            >
              {recording ? "Stop" : "Record"}
            </Button>

            {!reRecording && (
              <Button
                variant="outlined"
                onClick={uploadVideo}
                style={{ marginLeft: "8px" }}
                disabled={!video || uploading || reRecording}
              >
                {uploading && <Loading size={16} />}
                {!reRecording ? (hasSaveButton ? "Save" : "Stop") : "Stop"}
              </Button>
            )}
          </div>
        )} */}
      </Wrapper>
    );
  }
);

// Add display name for better debugging
VideoRecorder.displayName = 'VideoRecorder';

export default VideoRecorder;
